"use client";
import React, { createContext, useState, useContext, useEffect } from 'react';
import Cookies from 'js-cookie';
import { User } from '../models/User';
import { RecordItem } from '../models/RecordItem';

interface AppContextType {
    // Session Management
    token?: string | null;
    setToken?: React.Dispatch<React.SetStateAction<string | null>>;
    user: User | null;
    setUser: React.Dispatch<React.SetStateAction<User | null>>;
  
    // Navigation
    previousPage: string | null;
    currentPage: string | null;
    setPreviousPage: React.Dispatch<React.SetStateAction<string | null>>;
    setCurrentPage: React.Dispatch<React.SetStateAction<string | null>>;

    // Selected Record (global)
    selectedRecord: RecordItem | null;
    setSelectedRecord: React.Dispatch<React.SetStateAction<RecordItem | null>>;
}

const AppContext = React.createContext<AppContextType | undefined>(undefined);

// 
export function AppWrapper({ children }: { children: React.ReactNode }) {

    const [token, setToken] = useState<string | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [previousPage, setPreviousPage] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<string | null>(null);
    const [selectedRecord, setSelectedRecord] = useState<RecordItem | null>(null);

    // Load state from cookies on client side only
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const savedToken = Cookies.get('token');
            if (savedToken) {
                setToken(savedToken);
            }

            const savedUserData = Cookies.get('user_data');
            if (savedUserData) {
                try {
                    const parsedUser = new User(JSON.parse(savedUserData));
                    setUser(parsedUser);
                } catch (error) {
                    console.error('Failed to parse user data from cookies');
                }
            }

            const savedPreviousPage = Cookies.get('previousPage') ?? null;
            const savedCurrentPage = Cookies.get('currentPage') ?? null;

            setPreviousPage(savedPreviousPage);
            setCurrentPage(savedCurrentPage);

            // Load selected record from session storage (persists across page refresh but not browser close)
            const savedRecord = sessionStorage.getItem('selectedRecord');
            if (savedRecord) {
                try {
                    setSelectedRecord(new RecordItem(JSON.parse(savedRecord)));
                } catch (error) {
                    console.error('Failed to parse selected record');
                }
            }
        }
    }, []);

    // Save to cookies whenever state changes
    useEffect(() => {
        if (token) {
            Cookies.set('token', token, { expires: 7 }); // 7 days expiry
        } else {
            Cookies.remove('token');
        }
        if (user) {
            Cookies.set('user_data', JSON.stringify(user));
        } else {
            Cookies.remove('user_data');
        }

        if (previousPage) {
            Cookies.set('previousPage', previousPage);
        } else {
            Cookies.remove('previousPage');
        }

        if (currentPage) {
            Cookies.set('currentPage', currentPage);
        } else {
            Cookies.remove('currentPage');
        }
    }, [token, user, previousPage, currentPage]);

    // Save selected record to session storage
    useEffect(() => {
        if (selectedRecord) {
            sessionStorage.setItem('selectedRecord', JSON.stringify(selectedRecord));
        } else {
            sessionStorage.removeItem('selectedRecord');
        }
    }, [selectedRecord]);

    return (
        <AppContext.Provider value={{
            token,
            setToken,
            user,
            setUser,
            previousPage,
            currentPage,
            setPreviousPage,
            setCurrentPage,
            selectedRecord,
            setSelectedRecord,
        }}>
            {children}
        </AppContext.Provider>
    );
}

// Safe hook
export function useAppContext() {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("useAppContext must be used within an AppWrapper");
    }
    return context;
}

